import { apiGet, apiPost, apiPatch, apiDelete } from './apiClient';

export interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  logo_full_url?: string;
  address?: string;
  phone?: string;
  storage_used: number;
  storage_limit?: number;
  mollie_customer_id?: string;
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'standard';
  user: {
    id: string;
    email: string;
    full_name?: string;
  };
}

export interface OrganizationWithMembers extends Organization {
  members: OrganizationMember[];
}

export interface OrganizationCreate {
  name: string;
  logo_url?: string;
  address?: string;
  phone?: string;
}

export interface OrganizationUpdate {
  name?: string;
  logo_url?: string;
  address?: string;
  phone?: string;
}

export interface OrganizationMemberCreate {
  user_id: string;
  role: 'owner' | 'admin' | 'standard';
}

export interface OrganizationMemberUpdate {
  role: 'owner' | 'admin' | 'standard';
}

// Get user's organizations
export const fetchUserOrganizations = async (): Promise<Organization[]> => {
  return apiGet<Organization[]>('/organizations', { requiresAuth: true });
};

// Alias for compatibility with existing code
export const getOrganizations = fetchUserOrganizations;

// Get single organization with members
export const fetchOrganization = async (organizationId: string): Promise<OrganizationWithMembers> => {
  return apiGet<OrganizationWithMembers>(`/organizations/${organizationId}`, { requiresAuth: true });
};

// Alias for compatibility
export const getOrganization = fetchOrganization;

// Create new organization
export const createOrganization = async (data: OrganizationCreate): Promise<Organization> => {
  return apiPost<Organization>('/organizations', data, { requiresAuth: true });
};

// Update organization
export const updateOrganization = async (organizationId: string, data: OrganizationUpdate): Promise<Organization> => {
  return apiPatch<Organization>(`/organizations/${organizationId}`, data, { requiresAuth: true });
};

// Delete organization
export const deleteOrganization = async (organizationId: string): Promise<void> => {
  return apiDelete(`/organizations/${organizationId}`, { requiresAuth: true });
};

// Upload organization logo
export const uploadOrganizationLogo = async (organizationId: string, file: File): Promise<Organization> => {
  const formData = new FormData();
  formData.append('file', file);

  return apiPost<Organization>(`/organizations/${organizationId}/logo`, formData, { requiresAuth: true });
};

// Organization member management
export const addOrganizationMember = async (
  organizationId: string,
  data: OrganizationMemberCreate
): Promise<OrganizationMember> => {
  return apiPost<OrganizationMember>(`/organizations/${organizationId}/members`, data, { requiresAuth: true });
};

export const updateOrganizationMember = async (
  organizationId: string,
  userId: string,
  data: OrganizationMemberUpdate
): Promise<OrganizationMember> => {
  return apiPatch<OrganizationMember>(`/organizations/${organizationId}/members/${userId}`, data, { requiresAuth: true });
};

export const removeOrganizationMember = async (
  organizationId: string,
  userId: string
): Promise<void> => {
  return apiDelete(`/organizations/${organizationId}/members/${userId}`, { requiresAuth: true });
};

// Leave organization
export const leaveOrganization = async (organizationId: string): Promise<void> => {
  return apiPost(`/organizations/${organizationId}/leave`, {}, { requiresAuth: true });
};

// Organization invitation management
export interface OrganizationInvitation {
  id: string;
  email: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'standard';
  invited_by: string;
  token: string;
  created_at: string;
  expires_at: string;
  accepted: boolean;
  accepted_at?: string;
  organization?: Organization;
}

export interface OrganizationInvitationCreate {
  email: string;
  role: 'owner' | 'admin' | 'standard';
}

// Create invitation
export const createInvitation = async (
  organizationId: string,
  data: OrganizationInvitationCreate
): Promise<OrganizationInvitation> => {
  return apiPost<OrganizationInvitation>(
    `/organizations/${organizationId}/invitations`,
    data,
    { requiresAuth: true }
  );
};

// Get all invitations for an organization
export const fetchInvitations = async (organizationId: string): Promise<OrganizationInvitation[]> => {
  return apiGet<OrganizationInvitation[]>(
    `/organizations/${organizationId}/invitations`,
    { requiresAuth: true }
  );
};

// Revoke invitation
export const revokeInvitation = async (
  organizationId: string,
  invitationId: string
): Promise<void> => {
  return apiDelete(
    `/organizations/${organizationId}/invitations/${invitationId}`,
    { requiresAuth: true }
  );
};

// Get invitation by token (public endpoint)
export const fetchInvitationByToken = async (token: string): Promise<OrganizationInvitation> => {
  return apiGet<OrganizationInvitation>(`/invitations/${token}`, { requiresAuth: false });
};

// Accept invitation
export const acceptInvitation = async (token: string): Promise<{
  message: string;
  organization_id: string;
  role: string;
}> => {
  return apiPost(`/invitations/${token}/accept`, {}, { requiresAuth: true });
};